/**
 * Imposto, margem e arredondamento.
 *
 * A tela tem um simulador ao lado do formulário de propósito: o efeito de mudar a margem é um
 * número, não uma sensação, e ver o preço mudar enquanto se digita é o que evita salvar uma
 * configuração que ninguém conferiu.
 *
 * O cálculo roda **aqui**, sem ida ao servidor — é a cópia de `types/pricing.ts`, guardada por um
 * teste que compara com o backend.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { PageShell, SectionCard, InfoItem } from '../../components';
import pricingService from '../../services/pricingService';
import { calcularPreco, emReais } from '../../types/pricing';

/** Os degraus de arredondamento que fazem sentido comercialmente. */
const ARREDONDAMENTOS = [
  { valor: 0, rotulo: 'Sem arredondar (preço exato)' },
  { valor: 0.5, rotulo: 'Para cima, até R$ 0,50' },
  { valor: 1, rotulo: 'Para cima, até R$ 1,00' },
  { valor: 5, rotulo: 'Para cima, até R$ 5,00' },
];

export const PricingSettingsPage: React.FC = () => {
  const [imposto, setImposto] = useState('16');
  const [margem, setMargem] = useState('20');
  const [arredondar, setArredondar] = useState('0');
  const [custoSimulado, setCustoSimulado] = useState('25');

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const c = await pricingService.configuracao();
      setImposto(String(Number(c.taxPercentage)));
      setMargem(String(Number(c.profitMarginPercentage)));
      setArredondar(String(Number(c.roundUpToNearest)));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar a configuração');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const simulacao = useMemo(
    () =>
      calcularPreco({
        custo: Number(custoSimulado.replace(',', '.')) || 0,
        impostoPercentual: Number(imposto.replace(',', '.')) || 0,
        margemPercentual: Number(margem.replace(',', '.')) || 0,
        arredondarPara: Number(arredondar) || 0,
      }),
    [custoSimulado, imposto, margem, arredondar]
  );

  async function salvar(): Promise<void> {
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    try {
      await pricingService.salvarConfiguracao({
        taxPercentage: Number(imposto.replace(',', '.')),
        profitMarginPercentage: Number(margem.replace(',', '.')),
        roundUpToNearest: Number(arredondar),
      });
      setSalvo(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  const impossivel = simulacao === null;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Precificação' }]}
      title="Precificação"
      subtitle="O imposto e a margem que transformam o custo negociado com a clínica em preço de venda"
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}
      {salvo && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSalvo(false)}>
          Configuração salva. Os preços das clínicas passam a ser calculados com estes valores.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <SectionCard title="A configuração" icon={<CalculateIcon />}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Imposto sobre a venda (%)"
              value={imposto}
              onChange={(e) => setImposto(e.target.value)}
              disabled={carregando}
              fullWidth
            />
            <TextField
              label="Lucro desejado (%)"
              value={margem}
              onChange={(e) => setMargem(e.target.value)}
              helperText="Sobre o preço de venda, não sobre o custo."
              disabled={carregando}
              fullWidth
            />
            <TextField
              select
              label="Arredondamento"
              value={arredondar}
              onChange={(e) => setArredondar(e.target.value)}
              helperText="Sempre para cima — para baixo comeria a margem."
              disabled={carregando}
              fullWidth
            >
              {ARREDONDAMENTOS.map((a) => (
                <MenuItem key={a.valor} value={String(a.valor)}>
                  {a.rotulo}
                </MenuItem>
              ))}
            </TextField>

            {impossivel && (
              <Alert severity="warning">
                Imposto e margem somam 100% ou mais. Não existe preço que pague os dois e ainda
                cubra o custo.
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={() => void salvar()}
              disabled={salvando || carregando || impossivel}
            >
              Salvar
            </Button>
          </Stack>
        </SectionCard>

        <SectionCard title="Simulador" icon={<CalculateIcon />} accentColor="#1976d2">
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Custo do exame na clínica (R$)"
              value={custoSimulado}
              onChange={(e) => setCustoSimulado(e.target.value)}
              fullWidth
            />

            {simulacao && (
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {emReais(simulacao.preco)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  é o preço de venda
                </Typography>

                <Stack spacing={1}>
                  <InfoItem label="Custo (vai para a clínica)" value={emReais(simulacao.custo)} />
                  <InfoItem label="Imposto" value={emReais(simulacao.imposto)} />
                  <InfoItem label="Sobra para você" value={emReais(simulacao.lucro)} />
                  <InfoItem
                    label="Margem real"
                    value={`${simulacao.margemEfetiva.toLocaleString('pt-BR')}%`}
                  />
                  {simulacao.preco !== simulacao.precoExato && (
                    <InfoItem
                      label="Preço exato (antes de arredondar)"
                      value={emReais(simulacao.precoExato)}
                    />
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </SectionCard>
      </Box>
    </PageShell>
  );
};

export default PricingSettingsPage;
